import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import storage from '@/utils/storage';
import { useScreenSize } from '@/hooks';
import LoggedInVip from './LoggedInVip/LoggedInVip';
import styles from './VipClub.module.css';

/**
 * VIP Club Page Component
 * Shows sign-up page when not logged in
 * Opens VIP modal when logged in via query params: /vip-club?modal=vip
 */
const VipClub = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const modal = searchParams.get('modal');
  const { isMobile } = useScreenSize();

  useEffect(() => {
    // Check localStorage on component mount
    const checkLoginStatus = () => {
      const logged = storage.getKey('logged');
      const token = storage.getKey('token');
      const loginStatus = logged === true || logged === 'true' || (token && token !== null);
      setIsLoggedIn(loginStatus);
      
      // If logged in and no modal param, automatically open VIP modal
      if (loginStatus && !modal && location.pathname === '/vip-club') {
        navigate('/vip-club?modal=vip', { replace: true });
      }
    };

    checkLoginStatus();

    // Add event listener for storage changes
    const handleStorageChange = () => {
      checkLoginStatus();
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen to custom events (for same-tab updates)
    window.addEventListener('login-status-changed', handleStorageChange);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('login-status-changed', handleStorageChange);
    };
  }, [modal, location.pathname, navigate]);

  const handleSignUp = () => {
    navigate('/login?modalType=auth-signup');
  };

  const features = [
    {
      title: 'No-Fee Withdrawal',
      description: 'Reach VIP 38 for no-fee cryptocurrency withdrawals.',
      image: '/assets/images/vip/c1.webp',
    },
    {
      title: 'Rewarding Lucky Spin',
      description: 'Spin the wheel & receive bigger prizes as you level up.',
      image: '/assets/images/vip/c2.webp',
    },
    {
      title: 'Dedicated VIP Host',
      description: 'Selected VIPs get personalized attention from a dedicated host.',
      image: '/assets/images/vip/c3.webp',
    },
    {
      title: 'Amazing Level Up Bonus',
      description: 'Unlock multiple surprise rewards as you level up.',
      image: '/assets/images/vip/c4.webp',
    },
    {
      title: 'Fun Tips & Raining',
      description: 'Tip to show appreciation or try Coin Drops to win rewards.',
      image: '/assets/images/vip/c6.webp',
    },
    {
      title: 'Exclusive Cashback',
      description: 'Earn incredible cashback rewards every week & month.',
      image: '/assets/images/vip/c5.webp',
    },
  ];

  // Show VIP modal if logged in and modal param is present
  // Show sign-up page for non-logged-in users or when modal is not open
  return (
    <>
      {/* VIP Modal - Rendered to overlay everything when logged in */}
      {isLoggedIn && <LoggedInVip />}
      
      {/* Sign-up page content - only show when not logged in */}
      {!isLoggedIn && (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.content}>
              <h1 className={styles.title}>Exclusive VIP Benefits</h1>
              <p className={styles.description}>
                Join our VIP Club now and get ready to be showered with gifts, giveaways,
                and amazing features. Experience the thrill of higher cash back and exclusive
                rewards that are sure to leave you amazed.
              </p>
              <button className={styles.signUpButton} onClick={handleSignUp}>
                Sign Up
              </button>
            </div>
            <div className={styles.radialBackground} />
            <div className={styles.imageContainer}>
              <div className={styles.star} />
              <div className={styles.star} />
              <div className={styles.star} />
              <img
                src="/assets/images/vip/v1.png"
                alt="VIP Benefits illustration"
                className={styles.heroImage}
              />
            </div>
          </div>

          <div className={styles.cardContainer}>
            {features.map((feature, index) => (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureImageContainer}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className={styles.featureImage}
                  />
                </div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default VipClub;
